import React, { useState, useEffect, useContext, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Box from "@material-ui/core/Box";
import Tooltip from "@material-ui/core/Tooltip";
import Alert from "@material-ui/lab/Alert";
import VisibilityIcon from "@material-ui/icons/Visibility";
import MergeTypeIcon from "@material-ui/icons/MergeType";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import WarningIcon from "@material-ui/icons/Warning";
import RefreshIcon from "@material-ui/icons/Refresh";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import DeleteIcon from "@material-ui/icons/Delete";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  tableCell: {
    fontSize: "0.8rem",
    padding: "4px 8px",
  },
  tableCellCompact: {
    fontSize: "0.75rem",
    padding: "2px 6px",
    textAlign: "center",
  },
  contactCard: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  contactName: {
    fontWeight: 600,
    fontSize: "0.85rem",
  },
  contactNumber: {
    fontFamily: "monospace",
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },
  chipSafe: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    fontSize: "0.7rem",
    height: 22,
  },
  chipWarning: {
    backgroundColor: "#fff3e0",
    color: "#e65100",
    fontSize: "0.7rem",
    height: 22,
  },
  statsBox: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  statCard: {
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    minWidth: 120,
    textAlign: "center",
  },
  statNumber: {
    fontSize: "1.8rem",
    fontWeight: 700,
  },
  statLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  previewSection: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(1),
  },
  previewArrow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
    margin: theme.spacing(2, 0),
  },
  previewContact: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    flex: 1,
    textAlign: "center",
  },
  keepContact: {
    border: `2px solid ${theme.palette.success.main}`,
    backgroundColor: "#e8f5e9",
  },
  deleteContact: {
    border: `2px solid ${theme.palette.error.main}`,
    backgroundColor: "#ffebee",
  },
  transferList: {
    padding: theme.spacing(1),
    "& li": {
      marginBottom: 4,
      fontSize: "0.9rem",
    },
  },
  batchBar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

const ContactDuplicates = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [duplicates, setDuplicates] = useState({ pairs: [], totalPairs: 0, autoMergeablePairs: 0 });
  const [selectedPairs, setSelectedPairs] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergeResultOpen, setMergeResultOpen] = useState(false);
  const [mergeResult, setMergeResult] = useState(null);

  const fetchDuplicates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contacts-duplicates", { timeout: 120000 });
      setDuplicates(data);
      setSelectedPairs([]);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDuplicates();
  }, [fetchDuplicates]);

  const handleTogglePair = (pair) => {
    setSelectedPairs((prev) => {
      const key = `${pair.contactWithNine.id}-${pair.contactWithoutNine.id}`;
      const exists = prev.find(
        (p) => `${p.contactWithNine.id}-${p.contactWithoutNine.id}` === key
      );
      if (exists) {
        return prev.filter(
          (p) => `${p.contactWithNine.id}-${p.contactWithoutNine.id}` !== key
        );
      }
      return [...prev, pair];
    });
  };

  const handleSelectAllSafe = () => {
    const safePairs = duplicates.pairs.filter((p) => p.canAutoMerge);
    setSelectedPairs(safePairs);
  };

  const handleDeselectAll = () => {
    setSelectedPairs([]);
  };

  const isPairSelected = (pair) => {
    return selectedPairs.some(
      (p) =>
        p.contactWithNine.id === pair.contactWithNine.id &&
        p.contactWithoutNine.id === pair.contactWithoutNine.id
    );
  };

  const handlePreview = async (pair) => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    setPreviewData(null);
    try {
      const { data } = await api.get(
        `/contacts-duplicates/preview/${pair.contactWithNine.id}/${pair.contactWithoutNine.id}`
      );
      setPreviewData({ ...data, pair });
    } catch (err) {
      toastError(err);
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleMergeSingle = async (pair) => {
    setMerging(true);
    try {
      const { data } = await api.post("/contacts-duplicates/merge", {
        contactId1: pair.contactWithNine.id,
        contactId2: pair.contactWithoutNine.id,
      });
      toast.success(
        `${i18n.t("contactDuplicates.mergeSuccess")} (ID ${data.keepContactId})`
      );
      setPreviewOpen(false);
      fetchDuplicates();
    } catch (err) {
      toastError(err);
    } finally {
      setMerging(false);
    }
  };

  const handleMergeBatch = async () => {
    if (selectedPairs.length === 0) return;

    setMerging(true);
    try {
      const pairIds = selectedPairs.map((p) => ({
        contactId1: p.contactWithNine.id,
        contactId2: p.contactWithoutNine.id,
      }));

      const { data } = await api.post("/contacts-duplicates/merge-all", { pairIds });
      setMergeResult(data);
      setMergeResultOpen(true);
      fetchDuplicates();
    } catch (err) {
      toastError(err);
    } finally {
      setMerging(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("contactDuplicates.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="default"
            onClick={fetchDuplicates}
            disabled={loading}
            startIcon={<RefreshIcon />}
          >
            {i18n.t("contactDuplicates.buttons.refresh")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {/* Stats cards */}
      <Box className={classes.statsBox}>
        <Paper className={classes.statCard} elevation={2}>
          <Typography className={classes.statNumber} color="primary">
            {duplicates.totalPairs}
          </Typography>
          <Typography className={classes.statLabel}>
            {i18n.t("contactDuplicates.stats.totalPairs")}
          </Typography>
        </Paper>
        <Paper className={classes.statCard} elevation={2}>
          <Typography className={classes.statNumber} style={{ color: "#2e7d32" }}>
            {duplicates.autoMergeablePairs}
          </Typography>
          <Typography className={classes.statLabel}>
            {i18n.t("contactDuplicates.stats.safePairs")}
          </Typography>
        </Paper>
        <Paper className={classes.statCard} elevation={2}>
          <Typography className={classes.statNumber} style={{ color: "#e65100" }}>
            {duplicates.totalPairs - duplicates.autoMergeablePairs}
          </Typography>
          <Typography className={classes.statLabel}>
            {i18n.t("contactDuplicates.stats.warningPairs")}
          </Typography>
        </Paper>
        <Paper className={classes.statCard} elevation={2}>
          <Typography className={classes.statNumber} color="secondary">
            {selectedPairs.length}
          </Typography>
          <Typography className={classes.statLabel}>
            {i18n.t("contactDuplicates.stats.selected")}
          </Typography>
        </Paper>
      </Box>

      {/* Batch action bar */}
      {selectedPairs.length > 0 && (
        <Box className={classes.batchBar}>
          <Typography variant="body2">
            <strong>{selectedPairs.length}</strong>{" "}
            {i18n.t("contactDuplicates.batch.pairsSelected")}
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={handleMergeBatch}
            disabled={merging}
            startIcon={merging ? <CircularProgress size={16} /> : <MergeTypeIcon />}
          >
            {i18n.t("contactDuplicates.batch.mergeSelected")}
          </Button>
          <Button size="small" onClick={handleDeselectAll} style={{ color: "white" }}>
            {i18n.t("contactDuplicates.batch.deselectAll")}
          </Button>
        </Box>
      )}

      {/* Selection helpers */}
      {duplicates.totalPairs > 0 && selectedPairs.length === 0 && (
        <Box mb={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleSelectAllSafe}
            startIcon={<CheckCircleIcon style={{ color: "#2e7d32" }} />}
          >
            {i18n.t("contactDuplicates.buttons.selectAllSafe")} ({duplicates.autoMergeablePairs})
          </Button>
        </Box>
      )}

      {/* Table */}
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell className={classes.tableCell}>
                {i18n.t("contactDuplicates.table.contactWithNine")}
              </TableCell>
              <TableCell className={classes.tableCellCompact}>
                {i18n.t("contactDuplicates.table.tickets")}
              </TableCell>
              <TableCell className={classes.tableCell}>
                {i18n.t("contactDuplicates.table.contactWithoutNine")}
              </TableCell>
              <TableCell className={classes.tableCellCompact}>
                {i18n.t("contactDuplicates.table.tickets")}
              </TableCell>
              <TableCell className={classes.tableCellCompact}>
                {i18n.t("contactDuplicates.table.status")}
              </TableCell>
              <TableCell className={classes.tableCellCompact}>
                {i18n.t("contactDuplicates.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={7} />
            ) : duplicates.pairs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary" style={{ padding: 20 }}>
                    {i18n.t("contactDuplicates.noDuplicates")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              duplicates.pairs.map((pair) => (
                <TableRow
                  key={`${pair.contactWithNine.id}-${pair.contactWithoutNine.id}`}
                  hover
                  selected={isPairSelected(pair)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={isPairSelected(pair)}
                      onChange={() => handleTogglePair(pair)}
                    />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <div className={classes.contactCard}>
                      <span className={classes.contactName}>
                        {pair.contactWithNine.name}
                      </span>
                      <span className={classes.contactNumber}>
                        {pair.contactWithNine.number}
                      </span>
                      <span style={{ fontSize: "0.7rem", color: "#999" }}>
                        ID: {pair.contactWithNine.id} | {formatDate(pair.contactWithNine.createdAt)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className={classes.tableCellCompact}>
                    <Tooltip title={`${pair.contactWithNine.openTicketCount} abertos`}>
                      <span>
                        {pair.contactWithNine.ticketCount}
                        {pair.contactWithNine.openTicketCount > 0 && (
                          <WarningIcon
                            style={{ fontSize: 14, color: "#e65100", marginLeft: 2, verticalAlign: "middle" }}
                          />
                        )}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <div className={classes.contactCard}>
                      <span className={classes.contactName}>
                        {pair.contactWithoutNine.name}
                      </span>
                      <span className={classes.contactNumber}>
                        {pair.contactWithoutNine.number}
                      </span>
                      <span style={{ fontSize: "0.7rem", color: "#999" }}>
                        ID: {pair.contactWithoutNine.id} | {formatDate(pair.contactWithoutNine.createdAt)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className={classes.tableCellCompact}>
                    <Tooltip title={`${pair.contactWithoutNine.openTicketCount} abertos`}>
                      <span>
                        {pair.contactWithoutNine.ticketCount}
                        {pair.contactWithoutNine.openTicketCount > 0 && (
                          <WarningIcon
                            style={{ fontSize: 14, color: "#e65100", marginLeft: 2, verticalAlign: "middle" }}
                          />
                        )}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell className={classes.tableCellCompact}>
                    {pair.canAutoMerge ? (
                      <Chip
                        size="small"
                        label={i18n.t("contactDuplicates.status.safe")}
                        className={classes.chipSafe}
                        icon={<CheckCircleIcon style={{ fontSize: 14, color: "#2e7d32" }} />}
                      />
                    ) : (
                      <Chip
                        size="small"
                        label={i18n.t("contactDuplicates.status.warning")}
                        className={classes.chipWarning}
                        icon={<WarningIcon style={{ fontSize: 14, color: "#e65100" }} />}
                      />
                    )}
                  </TableCell>
                  <TableCell className={classes.tableCellCompact}>
                    <Tooltip title={i18n.t("contactDuplicates.buttons.preview")}>
                      <IconButton size="small" onClick={() => handlePreview(pair)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={i18n.t("contactDuplicates.buttons.merge")}>
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => handleMergeSingle(pair)}
                        disabled={merging}
                      >
                        <MergeTypeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Preview Modal */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {i18n.t("contactDuplicates.preview.title")}
        </DialogTitle>
        <DialogContent dividers>
          {previewLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : previewData ? (
            <>
              <Alert severity="info" style={{ marginBottom: 16 }}>
                {i18n.t("contactDuplicates.preview.explanation")}
              </Alert>

              <Box className={classes.previewArrow}>
                <Paper className={`${classes.previewContact} ${classes.keepContact}`} elevation={2}>
                  <CheckCircleIcon style={{ color: "#2e7d32", fontSize: 32 }} />
                  <Typography variant="h6" style={{ color: "#2e7d32" }}>
                    {i18n.t("contactDuplicates.preview.keep")}
                  </Typography>
                  <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    {previewData.keepContact.name}
                  </Typography>
                  <Typography variant="body2" style={{ fontFamily: "monospace" }}>
                    {previewData.keepContact.number}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    ID: {previewData.keepContact.id}
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ marginTop: 8, fontWeight: 600, color: "#1565c0" }}
                  >
                    {i18n.t("contactDuplicates.preview.finalNumber")}: {previewData.keepContact.finalNumber}
                  </Typography>
                </Paper>

                <ArrowForwardIcon style={{ fontSize: 40, color: "#999" }} />

                <Paper className={`${classes.previewContact} ${classes.deleteContact}`} elevation={2}>
                  <DeleteIcon style={{ color: "#c62828", fontSize: 32 }} />
                  <Typography variant="h6" style={{ color: "#c62828" }}>
                    {i18n.t("contactDuplicates.preview.delete")}
                  </Typography>
                  <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    {previewData.deleteContact.name}
                  </Typography>
                  <Typography variant="body2" style={{ fontFamily: "monospace" }}>
                    {previewData.deleteContact.number}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    ID: {previewData.deleteContact.id}
                  </Typography>
                </Paper>
              </Box>

              <Paper className={classes.previewSection} elevation={0}>
                <Typography variant="subtitle2" gutterBottom>
                  {i18n.t("contactDuplicates.preview.transfers")}:
                </Typography>
                <ul className={classes.transferList}>
                  <li>
                    <strong>{previewData.ticketsToTransfer}</strong>{" "}
                    {i18n.t("contactDuplicates.preview.ticketsToTransfer")}
                  </li>
                  <li>
                    <strong>{previewData.messagesToTransfer}</strong>{" "}
                    {i18n.t("contactDuplicates.preview.messagesToTransfer")}
                  </li>
                  <li>
                    <strong>{previewData.customFieldsToTransfer}</strong>{" "}
                    {i18n.t("contactDuplicates.preview.customFieldsToTransfer")}
                  </li>
                  <li>
                    <strong>{previewData.walletsToTransfer}</strong>{" "}
                    {i18n.t("contactDuplicates.preview.walletsToTransfer")}
                  </li>
                  <li>
                    <strong>{previewData.tagsToTransfer}</strong>{" "}
                    {i18n.t("contactDuplicates.preview.tagsToTransfer")}
                  </li>
                </ul>
              </Paper>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)} color="default">
            {i18n.t("contactDuplicates.buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => previewData && handleMergeSingle(previewData.pair)}
            disabled={merging || !previewData}
            startIcon={merging ? <CircularProgress size={16} /> : <MergeTypeIcon />}
          >
            {i18n.t("contactDuplicates.buttons.confirmMerge")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Merge batch result Modal */}
      <Dialog
        open={mergeResultOpen}
        onClose={() => setMergeResultOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {i18n.t("contactDuplicates.mergeResult.title")}
        </DialogTitle>
        <DialogContent dividers>
          {mergeResult && (
            <>
              <Box display="flex" gap={2} mb={2}>
                <Alert
                  severity={mergeResult.errorCount === 0 ? "success" : "warning"}
                  style={{ flex: 1 }}
                >
                  <strong>{mergeResult.successCount}</strong>{" "}
                  {i18n.t("contactDuplicates.mergeResult.success")}
                  {mergeResult.errorCount > 0 && (
                    <>
                      {" | "}
                      <strong>{mergeResult.errorCount}</strong>{" "}
                      {i18n.t("contactDuplicates.mergeResult.errors")}
                    </>
                  )}
                </Alert>
              </Box>
              {mergeResult.errorCount > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {i18n.t("contactDuplicates.mergeResult.errorDetails")}:
                  </Typography>
                  {mergeResult.results
                    .filter((r) => !r.success)
                    .map((r, idx) => (
                      <Alert severity="error" key={idx} style={{ marginBottom: 4 }}>
                        IDs {r.pair.contactId1} + {r.pair.contactId2}: {r.error}
                      </Alert>
                    ))}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeResultOpen(false)} color="primary" variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default ContactDuplicates;
